import { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import ProfileAnalyticsService from '../profileAnalyticsService';
import { ProfileAnalyticsData } from '../types';

interface Props {
    instituteId: string | number;
    // Called with freshly-parsed data after a successful upload so the parent can
    // refresh the preview.
    onUploaded: (data: ProfileAnalyticsData) => void;
    lastFileName?: string;
    lastUpdatedAt?: string;
}

const UploadPanel = ({ instituteId, onUploaded, lastFileName, lastUpdatedAt }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState<File | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);

    const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setWarnings([]);
        if (file && !file.name.toLowerCase().endsWith('.xlsx')) {
            Swal.fire({ icon: 'error', title: 'Invalid file', text: 'Please select a .xlsx file.' });
            setSelected(null);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        setSelected(file);
    };

    const handleUpload = async () => {
        if (!selected) {
            Swal.fire({ icon: 'info', title: 'No file selected', text: 'Choose a .xlsx file first.' });
            return;
        }
        setBusy(true);
        setWarnings([]);
        try {
            const res = await ProfileAnalyticsService.uploadFile(instituteId, selected);
            if (res.success && res.data) {
                setWarnings(res.warnings || []);
                onUploaded(res.data);
                setSelected(null);
                if (inputRef.current) inputRef.current.value = '';
                Swal.fire({
                    icon: 'success',
                    title: 'Uploaded',
                    text: res.message || 'Profile Analytics updated.',
                    timer: 1400,
                    showConfirmButton: false,
                });
            } else {
                setWarnings(res.warnings || []);
                Swal.fire({ icon: 'error', title: 'Upload failed', text: res.message || 'Could not parse file.' });
            }
        } catch (err: any) {
            Swal.fire({
                icon: 'error',
                title: 'Upload failed',
                text: err?.message || 'Something went wrong while uploading.',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="white-block p-3 p-md-4 mb-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                    <h3 className="font-18 font-600 mb-1">Upload Profile Analytics Dashboard</h3>
                    <p className="font-13 text-muted mb-0">
                        Upload the institute's <strong>profile_analytics_template.xlsx</strong>. Re-uploading
                        replaces the existing dashboard.
                    </p>
                    {lastFileName && (
                        <p className="font-12 text-muted mb-0 mt-1">
                            Current: <strong>{lastFileName}</strong>
                            {lastUpdatedAt ? ` · updated ${new Date(lastUpdatedAt).toLocaleString()}` : ''}
                        </p>
                    )}
                </div>
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <a
                        href="https://ags-qa-bucket.s3.ap-south-1.amazonaws.com/profile_analytics_template.xlsx"
                        className="btn btn-outline-primary"
                        download="profile_analytics_template.xlsx">
                        <span className="d-flex align-items-center">
                            <i className="bi bi-cloud-arrow-down-fill font-16 me-2" aria-hidden="true"></i>
                            Download Sample File
                        </span>
                    </a>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx"
                        className="form-control font-13"
                        style={{ maxWidth: 280 }}
                        onChange={handleSelect}
                        disabled={busy}
                    />
                    <button className="btn btn-green" onClick={handleUpload} disabled={busy || !selected}>
                        <span>{busy ? 'Uploading…' : lastFileName ? 'Re-upload' : 'Upload'}</span>
                    </button>
                </div>
            </div>

            {warnings.length > 0 && (
                <div className="alert alert-warning mt-3 mb-0 font-13">
                    <strong>Some sections need attention:</strong>
                    <ul className="mb-0 mt-1">
                        {warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UploadPanel;
